import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Printer, Save, Send, Trash2, Scale, AlertTriangle,
  CheckCircle2, PenLine, Loader2, Download, Mail,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { AgreementDocument, DEFAULT_FORM } from "@/components/contract/AgreementDocument";
import { CONTRACT_STATUS } from "@/pages/ContractsPage";
import { StatusPill } from "@/components/common/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { downloadContractPdf, contractPdfBase64 } from "@/lib/contractPdf";

// ---------------------------------------------------------------------------
// Focused (full-screen, light) contract detail page.
//   • admin   → editable document + Save / Send-to-family / Print / Delete
//   • family  → read-only document + e-sign panel + Print / Download
// Rendered outside the dark app shell so the print output is a clean PDF.
// ---------------------------------------------------------------------------

const SIGN_BLOCKS = [
  { key: "parent1", label: "Parent / Guardian 1", formKey: "parent1Name" },
  { key: "parent2", label: "Parent / Guardian 2", formKey: "parent2Name", optional: true },
  { key: "athlete", label: "Athlete", formKey: "athleteName" },
];

export default function ContractDetailPage() {
  const { contractId } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  useDocumentTitle("OmniDraftLab · Advisory Agreement");

  const [contract, setContract] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  useEffect(() => {
    const style = document.createElement("style");
    style.id = "contract-hide-emergent";
    style.textContent = `[data-testid="made-with-emergent-badge"], #emergent-watermark { display: none !important; }`;
    document.head.appendChild(style);
    return () => { document.getElementById("contract-hide-emergent")?.remove(); };
  }, []);

  const apply = useCallback((c) => {
    setContract(c);
    setForm({ ...DEFAULT_FORM, ...(c.form || {}) });
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        // Non-admins hitting the doc marks it viewed; admins just load it.
        const { data } = isAdmin
          ? await api.get(`/contracts/${contractId}`)
          : await api.post(`/contracts/${contractId}/view`);
        if (active) apply(data);
      } catch (e) {
        toast.error(e?.response?.status === 403 ? "You don't have access to this contract" : "Contract not found");
        navigate(isAdmin ? "/app/contracts" : "/app/me/contracts");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [contractId, isAdmin, apply, navigate]);

  // edit handlers (admin)
  const set = (k, v) => { setForm((s) => ({ ...s, [k]: v })); setDirty(true); };
  const setPayment = (i, field, v) => {
    setForm((s) => { const next = s.payments.slice(); next[i] = { ...next[i], [field]: v }; return { ...s, payments: next }; });
    setDirty(true);
  };
  const addPayment = () => { setForm((s) => ({ ...s, payments: [...s.payments, { date: "", amount: "", note: "" }] })); setDirty(true); };
  const removePayment = (i) => { setForm((s) => ({ ...s, payments: s.payments.filter((_, idx) => idx !== i) })); setDirty(true); };

  const saveForm = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/contracts/${contractId}`, { form });
      apply(data); setDirty(false);
      toast.success("Saved");
      return data;
    } catch {
      toast.error("Failed to save"); return null;
    } finally { setSaving(false); }
  };

  const sendToFamily = async () => {
    if (dirty) { const ok = await saveForm(); if (!ok) return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/contracts/${contractId}`, { status: "sent" });
      apply(data);
      toast.success("Sent to the family's hub");
    } catch {
      toast.error("Failed to send");
    } finally { setSaving(false); }
  };

  const removeContract = async () => {
    if (!confirm("Delete this contract permanently? This cannot be undone.")) return;
    try { await api.delete(`/contracts/${contractId}`); toast.success("Contract deleted"); navigate("/app/contracts"); }
    catch { toast.error("Failed to delete"); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      await downloadContractPdf(form.athleteName);
      toast.success("PDF downloaded");
    } catch {
      toast.error("Couldn't generate the PDF");
    } finally { setDownloading(false); }
  };

  // Admin: check whether email sending is configured (controls the Email button).
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/contracts/config/email").then(({ data }) => setEmailEnabled(!!data.email_enabled)).catch(() => {});
  }, [isAdmin]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f4f1ea] grid place-items-center" data-testid="contract-detail-loading">
        <div className="flex items-center gap-3 text-black/60"><Loader2 className="h-5 w-5 animate-spin" /> Loading agreement…</div>
      </div>
    );
  }
  if (!contract) return null;

  const st = CONTRACT_STATUS[contract.status] || CONTRACT_STATUS.draft;
  const backTo = isAdmin ? "/app/contracts" : "/app/me/contracts";

  return (
    <div className="agreement-root min-h-screen bg-[#f4f1ea]" data-testid="contract-detail-page">
      {/* Toolbar */}
      <div className="ag-no-print sticky top-0 z-40 backdrop-blur-md bg-[#f4f1ea]/85 border-b border-black/10">
        <div className="max-w-[8.6in] mx-auto px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
          <button onClick={() => navigate(backTo)} className="inline-flex items-center gap-2 text-black/70 hover:text-black text-sm" data-testid="contract-back">
            <ArrowLeft className="h-4 w-4" /> Contracts
          </button>
          <div className="flex items-center gap-2">
            <StatusPill tone={st.tone} data-testid="contract-status-pill"><st.icon className="h-3 w-3" /> {st.label}</StatusPill>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isAdmin && (
              <>
                <Button variant="outline" size="sm" onClick={saveForm} disabled={saving || !dirty} data-testid="contract-save" className="bg-white border-black/15 text-black/80 hover:bg-black/5">
                  <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save"}
                </Button>
                <Button size="sm" onClick={sendToFamily} disabled={saving} data-testid="contract-send" className="bg-rose-600 text-white hover:bg-rose-700">
                  <Send className="h-4 w-4 mr-1.5" /> {contract.status === "draft" ? "Send to family" : "Re-send"}
                </Button>
                <Button size="sm" onClick={() => setEmailOpen(true)} data-testid="contract-email" className="bg-white border border-black/15 text-black/80 hover:bg-black/5">
                  <Mail className="h-4 w-4 mr-1.5" /> Email
                </Button>
              </>
            )}
            <Button size="sm" onClick={downloadPdf} disabled={downloading} data-testid="contract-download" className="bg-[#04111A] text-white hover:bg-[#0a1e2c]">
              {downloading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />} {downloading ? "Preparing…" : "Download PDF"}
            </Button>
            <button onClick={() => window.print()} data-testid="contract-print" className="p-2 rounded-lg text-black/50 hover:text-black hover:bg-black/5 transition" aria-label="Print" title="Print">
              <Printer className="h-4 w-4" />
            </button>
            {isAdmin && (
              <button onClick={removeContract} data-testid="contract-delete" className="p-2 rounded-lg text-black/40 hover:text-rose-700 hover:bg-rose-50 transition" aria-label="Delete contract">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Admin disclaimer banner */}
      {isAdmin && (
        <div className="ag-no-print max-w-[8.6in] mx-auto px-5 pt-5">
          <div className="rounded-xl border-2 border-amber-400 bg-amber-50 px-4 py-3 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="text-[13px] leading-snug text-amber-900">
              <strong>Template — not legal advice.</strong> Have this reviewed by a Tennessee-licensed sports / contracts attorney before sending. Fill in the family details, tier, term, and payment schedule, then <strong>Send to family</strong> to push it to their hub.
            </div>
          </div>
        </div>
      )}

      {/* Family sign panel */}
      {!isAdmin && (
        <FamilySignPanel contract={contract} form={form} onSigned={apply} contractId={contractId} />
      )}

      <AgreementDocument
        form={form}
        set={isAdmin ? set : undefined}
        setPayment={isAdmin ? setPayment : undefined}
        addPayment={isAdmin ? addPayment : undefined}
        removePayment={isAdmin ? removePayment : undefined}
        readOnly={!isAdmin}
        sigMode="contract"
        signatures={contract.signatures || {}}
      />

      {isAdmin && emailOpen && (
        <EmailContractDialog
          contractId={contractId}
          form={form}
          emailEnabled={emailEnabled}
          onClose={() => setEmailOpen(false)}
          onSent={apply}
        />
      )}
    </div>
  );
}

function EmailContractDialog({ contractId, form, emailEnabled, onClose, onSent }) {
  const [to, setTo] = useState(form.familyEmail || "");
  const [cc, setCc] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!to.trim()) { toast.error("Enter a recipient email"); return; }
    setSending(true);
    try {
      const pdf_base64 = await contractPdfBase64();
      const payload = {
        pdf_base64,
        to: to.split(",").map((s) => s.trim()).filter(Boolean),
        cc: cc.split(",").map((s) => s.trim()).filter(Boolean),
        message: message.trim() || undefined,
      };
      const { data } = await api.post(`/contracts/${contractId}/email`, payload);
      toast.success(`Agreement emailed to ${data.to.join(", ")}`);
      onSent?.({ ...(await api.get(`/contracts/${contractId}`)).data });
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send email");
    } finally { setSending(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="contract-email-dialog">
        <DialogHeader>
          <DialogTitle>Email this agreement</DialogTitle>
          <DialogDescription>Sends the current agreement as a PDF attachment. Separate multiple addresses with commas.</DialogDescription>
        </DialogHeader>

        {!emailEnabled ? (
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-amber-900 text-sm" data-testid="contract-email-disabled">
            Email isn't set up yet. Add your SendGrid API key and a verified sender address to enable sending. You can still <strong>Download PDF</strong> and attach it manually.
          </div>
        ) : (
          <div className="space-y-3 py-1">
            <div>
              <Label className="text-xs">To</Label>
              <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="family@example.com" data-testid="contract-email-to" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Cc (optional — e.g. their attorney)</Label>
              <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="attorney@firm.com" data-testid="contract-email-cc" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Message (optional)</Label>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="A short note to include in the email…" data-testid="contract-email-message" className="mt-1" />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          {emailEnabled && (
            <Button onClick={send} disabled={sending} data-testid="contract-email-send" className="bg-rose-600 text-white hover:bg-rose-700">
              {sending ? "Sending…" : "Send email"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FamilySignPanel({ contract, form, onSigned, contractId }) {
  const sigs = contract.signatures || {};
  const fullySigned = contract.status === "signed";

  return (
    <div className="ag-no-print max-w-[8.6in] mx-auto px-5 pt-5">
      <div className="rounded-xl border border-black/10 bg-white p-5" data-testid="contract-sign-panel">
        <div className="flex items-center gap-2 mb-1">
          <Scale className="h-4 w-4 text-rose-600" />
          <h2 className="text-[15px] font-semibold text-black" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>Review &amp; sign your agreement</h2>
        </div>
        <p className="text-[13px] text-black/60 mb-4" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>
          Read the full agreement below. You can sign electronically here, or use <strong>Download PDF</strong> to save a copy, sign by hand, and return it.
        </p>

        {fullySigned ? (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 flex items-center gap-2 text-emerald-800 text-sm" data-testid="contract-fully-signed">
            <CheckCircle2 className="h-4 w-4" /> This agreement has been fully executed. Thank you — your advisor has been notified.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {SIGN_BLOCKS.map((b) => (
              <SignSlot key={b.key} block={b} signature={sigs[b.key]} prefill={form[b.formKey]} contractId={contractId} onSigned={onSigned} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SignSlot({ block, signature, prefill, contractId, onSigned }) {
  const [name, setName] = useState(prefill || "");
  const [busy, setBusy] = useState(false);
  const signed = !!signature?.signed_at;

  const sign = async () => {
    const n = (name || "").trim();
    if (!n) { toast.error("Type the full legal name to sign"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/contracts/${contractId}/sign`, { block: block.key, name: n });
      onSigned(data);
      toast.success(`Signed as ${block.label}`);
    } catch {
      toast.error("Failed to sign");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-black/10 p-3" data-testid={`sign-slot-${block.key}`} style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-rose-700 font-semibold">
        {block.label} {block.optional && <span className="text-black/40 normal-case font-normal italic">(if applicable)</span>}
      </div>
      {signed ? (
        <div className="mt-2">
          <div className="text-[20px] leading-none text-black" style={{ fontFamily: "'Segoe Script', 'Brush Script MT', cursive" }}>{signature.name}</div>
          <div className="mt-1 text-[11px] text-emerald-700 font-semibold inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Signed</div>
        </div>
      ) : (
        <div className="mt-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Full legal name"
            data-testid={`sign-input-${block.key}`}
            className="h-9 bg-white border-black/15 text-black text-sm"
          />
          <Button size="sm" onClick={sign} disabled={busy} data-testid={`sign-btn-${block.key}`} className="mt-2 w-full bg-rose-600 text-white hover:bg-rose-700">
            <PenLine className="h-3.5 w-3.5 mr-1.5" /> {busy ? "Signing…" : "Sign electronically"}
          </Button>
        </div>
      )}
    </div>
  );
}
